import { motion } from "framer-motion";

/**
 * Eye-catching wordmark for "جُملَتِي".
 * IMPORTANT: NO letter-spacing on Arabic — it breaks the cursive connection.
 * Width is controlled via font-stretch / scale-x for a balanced elongated look.
 */
const JumlatyLogo = ({ size = "lg", showTagline = true, tagline = "الجملة الذكية لتجار العراق", variant = "default" }) => {
  const sizes = {
    sm: { text: "text-lg sm:text-xl", tagline: "text-[10px]" },
    md: { text: "text-xl sm:text-2xl", tagline: "text-xs" },
    lg: { text: "text-2xl sm:text-3xl", tagline: "text-xs" },
    xl: { text: "text-3xl sm:text-4xl md:text-5xl", tagline: "text-sm" },
  };
  const s = sizes[size] || sizes.lg;

  // White-gold (champagne) variant for use on blue silk hero backgrounds
  const isGold = variant === "gold";
  const wordmarkGradient = isGold
    ? "linear-gradient(120deg, #FFFFFF 0%, #FFF4D6 25%, #F5DEB3 50%, #FFE9A8 75%, #FFFFFF 100%)"
    : "linear-gradient(120deg, #1E3A5F 0%, #3B82F6 35%, #0EA5E9 55%, #1E3A5F 100%)";
  const wordmarkShadow = isGold
    ? "drop-shadow(0 4px 16px rgba(212, 175, 55, 0.55)) drop-shadow(0 2px 4px rgba(0,0,0,0.45))"
    : "drop-shadow(0 4px 12px rgba(59,130,246,0.35))";
  const underlineGradient = isGold
    ? "linear-gradient(90deg, transparent 0%, #FFE9A8 30%, #F5DEB3 70%, transparent 100%)"
    : "linear-gradient(90deg, transparent 0%, #3B82F6 30%, #0EA5E9 70%, transparent 100%)";
  const taglineColor = isGold ? "text-white/95 drop-shadow" : "text-[#3B82F6]/80";

  return (
    <div className="inline-flex flex-col items-center" data-testid="jumlaty-logo">
      <div className="relative inline-flex items-center">
        {/* Main wordmark — NO letter-spacing on Arabic */}
        <motion.span
          animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
          className={`relative font-heading font-bold ${s.text} bg-clip-text text-transparent`}
          style={{
            backgroundImage: wordmarkGradient,
            backgroundSize: "200% 100%",
            filter: wordmarkShadow,
            lineHeight: 1.6,
            paddingTop: "0.15em",
            paddingBottom: "0.1em",
            fontFeatureSettings: '"liga" 1, "calt" 1, "mark" 1, "mkmk" 1',
          }}
        >
          جُملَتِي
        </motion.span>

        {/* Decorative sparkles */}
        <motion.span
          initial={{ y: 0, opacity: 0.4 }}
          animate={{ y: [-3, -8, -3], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          className={`absolute -top-3 -right-3 text-lg ${isGold ? "text-[#FFE9A8]" : "text-[#F5A623]"}`}
          aria-hidden
        >
          ✦
        </motion.span>
        <motion.span
          initial={{ y: 0, opacity: 0.4 }}
          animate={{ y: [3, 8, 3], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
          className={`absolute -bottom-3 -left-3 ${isGold ? "text-[#FFFFFF]" : "text-[#0EA5E9]"}`}
          aria-hidden
        >
          ✦
        </motion.span>
      </div>

      {/* Underline accent */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="h-[3px] w-3/4 mt-2 rounded-full origin-center"
        style={{
          background: underlineGradient,
        }}
      />

      {showTagline && (
        <motion.p
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`font-medium ${s.tagline} ${taglineColor} mt-2`}
        >
          {tagline}
        </motion.p>
      )}
    </div>
  );
};

export default JumlatyLogo;
